/**
 * Rendering numbers and dates, in the reader's locale.
 *
 * Here rather than in each feature because it was in two of them, with the
 * drift that always follows: one `day()` returned an em dash for a missing
 * value and the other returned the empty string, so the same absence rendered
 * differently on two screens of the same product. A third copy was about to be
 * written for the catalogue, which is what the boundary rule exists to stop —
 * shared surface moves *down* into a core package.
 *
 * Two properties hold throughout:
 *
 * **Nothing parses to `number` until display.** Money and quantities arrive as
 * decimal strings, deliberately: a JSON number cannot represent 0.10 exactly,
 * and a price that arrives as 0.10000000000000001 is a rounding argument at
 * invoice time.
 *
 * **A value that will not parse is returned unchanged.** A formatter is not
 * the place to discover that the API sent something unexpected; showing the
 * raw string is more useful than `NaN` and more honest than an em dash.
 *
 * The formatters are memoised per locale: `Intl.NumberFormat` is expensive to
 * construct and a table of fifty rows would otherwise build one per cell.
 */

/** What a missing value renders as. Not the empty string — an empty cell reads
 * as a layout bug, and an em dash reads as "there is no value". */
export const EM_DASH = "—";

const moneyCache = new Map<string, Intl.NumberFormat>();
const quantityCache = new Map<string, Intl.NumberFormat>();
const percentCache = new Map<string, Intl.NumberFormat>();

/**
 * Whether a string is a number this locale can render.
 *
 * The empty-string check is the one that matters: `Number("")` is 0, so
 * without it an absent amount renders as "0,00" — which is not a formatting
 * quirk but a false statement about a price.
 */
function parse(value: string): number | null {
  if (value.trim() === "") return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function cached(
  cache: Map<string, Intl.NumberFormat>,
  locale: string,
  options: Intl.NumberFormatOptions,
): Intl.NumberFormat {
  let formatter = cache.get(locale);
  if (!formatter) {
    formatter = new Intl.NumberFormat(locale, options);
    cache.set(locale, formatter);
  }
  return formatter;
}

/** The narrow no-break space `Intl` groups French thousands with. */
const NARROW_NO_BREAK_SPACE = /\u202f/g;

/**
 * `Intl`'s grouping separator, replaced by a space the brand font can draw.
 *
 * `Intl.NumberFormat("fr")` groups thousands with U+202F, a NARROW no-break
 * space. Typographically it is the right character, and it was invisible in
 * this product: Plus Jakarta Sans has no glyph for it, so the browser drew it
 * about one pixel wide and every amount over a thousand rendered as
 * "138492,00" — the exact misreading a thousands separator exists to prevent,
 * on the screens where a misread figure is a telephone call to the accounts
 * department. Measured on the rendered page: 1.2px for U+202F against 2.4px
 * for U+00A0, which is legible.
 *
 * U+00A0 is still a no-break space, so an amount never wraps mid-number.
 *
 * The backend made the mirror image of this mistake for the same reason and
 * arrived at the same character: U+202F is absent from CP1252, so a PDF
 * printed it as `?`. See `platform/pdf` and `billing/interfaces/composition`.
 */
function grouped(text: string): string {
  return text.replace(NARROW_NO_BREAK_SPACE, "\u00a0");
}

/**
 * A money amount, with two decimals.
 *
 * The currency symbol is deliberately absent. It is an installation setting,
 * and a hard-coded "MAD" would be wrong for the second deployment; the column
 * header carries the unit instead, once, where it belongs.
 */
export function money(value: string, locale: string): string {
  const parsed = parse(value);
  return parsed === null
    ? value
    : grouped(
        cached(moneyCache, locale, {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }).format(parsed),
      );
}

/** A quantity. Trailing zeros dropped: "12" reads better than "12,000". */
export function quantity(value: string, locale: string): string {
  const parsed = parse(value);
  return parsed === null
    ? value
    : grouped(cached(quantityCache, locale, { maximumFractionDigits: 3 }).format(parsed));
}

/** A percentage, with its sign. */
export function percent(value: string, locale: string): string {
  const parsed = parse(value);
  if (parsed === null) return value;
  return `${grouped(cached(percentCache, locale, { maximumFractionDigits: 2 }).format(parsed))} %`;
}

/**
 * A date.
 *
 * `fallback` is a parameter rather than a fixed em dash because the honest
 * rendering of "no value" depends on the column: a creation date that is
 * missing is a dash, and a last-sign-in that is missing is "never". Baking one
 * in led to `moment(...) || t("never")` at a call site, which works by
 * accident and breaks the moment the fallback stops being falsy.
 */
export function day(value: string | null, locale: string, fallback: string = EM_DASH): string {
  if (!value) return fallback;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime())
    ? value
    : parsed.toLocaleDateString(locale, { year: "numeric", month: "short", day: "numeric" });
}

/** A date and a time. */
export function moment(value: string | null, locale: string, fallback: string = EM_DASH): string {
  if (!value) return fallback;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime())
    ? value
    : parsed.toLocaleString(locale, { dateStyle: "medium", timeStyle: "short" });
}
